import { useState } from "react";
import { z } from "zod";
import { Lesson, RevisionStats } from "@ca/shared";
import { Card, CardHead, Chip, Modal, PageHead } from "@ca/ui";
import { LessonReader, type RevisionProps } from "./revision-theory.tsx";
import { Failed, useRead } from "./revision-ui.tsx";
import { featureOn } from "./features.ts";
/** Phần trăm làm tròn sao cho cộng lại đúng 100 (chia phần dư lớn nhất), như bản mẫu. */
function pctList(values: number[], total: number) {
  const raw = values.map((v) => (v / total) * 100),
    base = raw.map(Math.floor);
  let left = 100 - base.reduce((a, b) => a + b, 0);
  raw
    .map((v, i) => [v - (base[i] ?? 0), i] as const)
    .sort((x, y) => y[0] - x[0])
    .forEach(([, i]) => {
      if (left > 0) {
        base[i] = (base[i] ?? 0) + 1;
        left--;
      }
    });
  return base;
}
type Slice = { key: string; title: string; wrong: number };
/** Biểu đồ tròn 7 màu cố định `--pc1..7`, khe 2px giữa các lát (`pieSVG` của bản mẫu). */
function PieSvg({ rows, label, size = 150 }: { rows: Slice[]; label: string; size?: number }) {
  const total = rows.reduce((n, r) => n + r.wrong, 0),
    c = size / 2,
    r = c - 2,
    pcts = pctList(
      rows.map((x) => x.wrong),
      total || 1,
    );
  let a0 = -Math.PI / 2;
  return (
    <svg
      className="pie"
      viewBox={`0 0 ${size} ${size}`}
      width={size}
      height={size}
      role="img"
      aria-label={`${label}: biểu đồ tròn ${rows.length} phần`}
    >
      {!total && <circle cx={c} cy={c} r={r} style={{ fill: "var(--surface-3)" }} />}
      {rows.map((row, i) => {
        const f = row.wrong / total,
          a1 = a0 + f * 2 * Math.PI;
        const attrs = {
          className: "pie-s",
          style: { fill: `var(--pc${(i % 7) + 1})` },
          tabIndex: 0,
          "data-tip": `${label} · ${row.title}|${row.wrong} câu · ${pcts[i] ?? 0}% lỗi`,
          "aria-label": `${row.title}: ${row.wrong} câu, ${pcts[i] ?? 0}%`,
        };
        const p0 = [c + r * Math.cos(a0), c + r * Math.sin(a0)],
          p1 = [c + r * Math.cos(a1), c + r * Math.sin(a1)],
          large = a1 - a0 > Math.PI ? 1 : 0;
        a0 = a1;
        return f > 0.9999 ? (
          <circle key={row.key} cx={c} cy={c} r={r} {...attrs} />
        ) : (
          <path
            key={row.key}
            d={`M${c},${c}L${(p0[0] ?? 0).toFixed(2)},${(p0[1] ?? 0).toFixed(2)}A${r},${r} 0 ${large} 1 ${(p1[0] ?? 0).toFixed(2)},${(p1[1] ?? 0).toFixed(2)}Z`}
            {...attrs}
          />
        );
      })}
    </svg>
  );
}
function PieLegend({
  rows,
  lessons,
  prefix = "",
  open,
}: {
  rows: Slice[];
  /** Bài trị lỗi theo khoá (`5|Giới từ`, `trap|echo`) từ `/v1/lessons`. */
  lessons: Map<string, Lesson>;
  prefix?: string;
  open: (key: string) => void;
}) {
  const total = rows.reduce((n, r) => n + r.wrong, 0),
    pcts = pctList(
      rows.map((x) => x.wrong),
      total || 1,
    );
  return (
    <ul className="pie-leg">
      {rows.map((r, i) => {
        const lesson = lessons.get(prefix + r.key);
        return (
          <li key={r.key}>
            <i style={{ background: `var(--pc${(i % 7) + 1})` }} aria-hidden="true" />
            <span className="pl-n">{r.title}</span>
            <span className="pl-v num">
              {r.wrong} · {pcts[i] ?? 0}%
            </span>
            <button
              className="chip acc"
              data-tip={
                lesson?.locked
                  ? "Bài trị lỗi dạng này dành cho gói Pro|Bấm để xem bài và cách nâng cấp"
                  : lesson?.total
                    ? `${lesson.total} bài trị lỗi đúng dạng này|Bấm để vào bài, giờ học tính từ lúc bài mở ra`
                    : "Bài trị lỗi đúng dạng này|Bấm để vào bài, giờ học tính từ lúc bài mở ra"
              }
              onClick={() => {
                open(r.key);
              }}
            >
              {lesson?.locked ? "🔒 Pro" : lesson?.total ? `${lesson.total} bài →` : "Trị lỗi →"}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
export function Errors(props: RevisionProps) {
  const { uid } = props;
  const q = useRead(uid, "/v1/me/stats/summary", RevisionStats);
  const [remedy, setRemedy] = useState<string | null>(null);
  const lessonList = useRead(uid, "/v1/lessons", z.array(Lesson));
  const lessons = new Map((lessonList.data ?? []).map((l) => [l.key, l]));
  const stats = q.data;
  const bySkillPart = (part: number) =>
    (stats?.skills ?? [])
      .filter((s) => s.part === part && s.wrong > 0)
      .sort((a, b) => b.wrong - a.wrong);
  const parts = featureOn("errors-listening") ? [1, 2, 3, 4, 5, 6, 7] : [5, 6, 7];
  const partsWithErrors = parts.filter((p) => bySkillPart(p).length);
  const shownParts = partsWithErrors.length ? partsWithErrors : [5, 6, 7];
  const traps = (stats?.traps ?? []).filter((t) => t.wrong > 0).sort((a, b) => b.wrong - a.wrong);
  const trapN = traps.reduce((n, t) => n + t.wrong, 0);
  const empty = !partsWithErrors.length && !trapN;
  return (
    <>
      <PageHead
        space="study"
        title="Sổ tay lỗi sai"
        desc="Cá gom câu sai theo từng dạng trong mỗi Part và theo loại bẫy, để biết bạn yếu dạng nào, sập bẫy nào nhất. Bấm “bài →” để mở bài trị lỗi đúng dạng đó."
      />
      <Failed error={q.error} />
      <div className="grid g3">
        <section className="tile y">
          <div className="t-sub">TỔNG CÂU ĐÃ LÀM</div>
          <div className="big">{stats?.total ?? 0}</div>
        </section>
        <section className="tile g">
          <div className="t-sub">ĐỘ CHÍNH XÁC</div>
          <div className="big">
            {stats?.total ? `${Math.round((stats.correct / stats.total) * 100)}%` : "—"}
          </div>
        </section>
        <section className="tile p">
          <div className="t-sub">NHÓM LỖI ĐANG TRỊ</div>
          <div className="big">{stats?.groups ?? 0}</div>
          <div className="t-sub">{stats?.open ?? 0} câu chưa thuộc</div>
        </section>
      </div>
      <Card>
        <CardHead
          title="🧬 Tổng hợp lỗi theo Part & theo bẫy"
          sub="Rê chuột (hoặc bấm Tab) vào từng phần để xem số câu; số liệu và nút mở bài trị lỗi nằm ngay bên dưới mỗi biểu đồ."
        >
          <Chip tone="acc">AI tự gán nhãn</Chip>
        </CardHead>
        {empty && (
          <p className="faint" style={{ fontSize: 13, fontWeight: 600 }}>
            Làm một bài luyện để Cá biết phần cần ôn.
          </p>
        )}
        <div className="eyebrow" style={{ margin: "16px 0 8px" }}>
          A · Theo dạng câu hỏi
        </div>
        <div className="grid g3">
          {shownParts.map((p) => {
            const rows = bySkillPart(p),
              n = rows.reduce((a, r) => a + r.wrong, 0);
            return (
              <div key={p} className="pie-card">
                <div className="row between">
                  <b>Part {p}</b>
                  <span className="faint" style={{ fontSize: 12.5, fontWeight: 700 }}>
                    {n} lỗi
                  </span>
                </div>
                <div className="pie-wrap">
                  <PieSvg rows={rows} label={`Part ${p}`} />
                </div>
                <PieLegend rows={rows} lessons={lessons} open={setRemedy} />
              </div>
            );
          })}
        </div>
        <div className="eyebrow" style={{ margin: "20px 0 8px" }}>
          B · Theo loại bẫy hay sập · {trapN} lỗi
        </div>
        <div className="pie-card pie-wide">
          <div className="pie-wrap">
            <PieSvg rows={traps} label="Bẫy" size={190} />
          </div>
          {traps.length ? (
            <PieLegend
              rows={traps}
              lessons={lessons}
              prefix="trap|"
              open={(key) => {
                setRemedy(`trap|${key}`);
              }}
            />
          ) : (
            <p className="faint" style={{ fontSize: 13, fontWeight: 600 }}>
              Chưa sập bẫy nào.
            </p>
          )}
        </div>
      </Card>
      {remedy && (
        <Modal
          title="Trị lỗi"
          wide
          onClose={() => {
            setRemedy(null);
          }}
        >
          <LessonReader
            {...props}
            lessonKey={remedy}
            close={() => {
              setRemedy(null);
            }}
            remedy
          />
        </Modal>
      )}
    </>
  );
}
